import { useEffect, useMemo, useState } from "react";
import client, { apiError } from "@/api/client";
import { PageHeader, Section } from "@/components/PageChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useUserNames } from "@/hooks/useUserNames";
import { toast } from "sonner";
import { Archive as ArchiveBox, ArrowRight } from "@phosphor-icons/react";

export default function Packing() {
  const [pending, setPending] = useState([]);
  const [orderId, setOrderId] = useState("");
  const [confirmQty, setConfirmQty] = useState("");
  const { nameFor } = useUserNames();

  const load = async () => { const { data } = await client.get("/orders/pending-packing"); setPending(data); };
  useEffect(() => { load(); }, []);

  const selected = useMemo(() => pending.find(o => o.id === orderId), [pending, orderId]);
  const scannedQty = selected?.scanning?.total_pieces ?? 0;

  const submit = async () => {
    if (!orderId) return toast.error("Select an order");
    if (confirmQty === "" || confirmQty == null) return toast.error("Enter Confirm Quantity");
    if (Number(confirmQty) !== Number(scannedQty)) {
      return toast.error(`Confirm quantity ${confirmQty} must match scanned quantity ${scannedQty}`);
    }
    try {
      await client.post("/orders/pack", { order_id: orderId, confirm_quantity: Number(confirmQty) });
      toast.success("Packed"); setOrderId(""); setConfirmQty(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const mismatch = selected && confirmQty !== "" && Number(confirmQty) !== Number(scannedQty);

  return (
    <div>
      <PageHeader title="Packing" subtitle="Stage 4 · Packing Line" />
      <Section>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 bg-violet-50 border border-violet-200 border-l-4 border-l-violet-500 rounded-sm p-5">
            <div className="label-cap text-violet-800">New Packing Entry</div>
            <h3 className="font-display text-xl font-bold mt-1 text-violet-900 mb-4">Pack an order</h3>

            <label className="label-cap">Order No</label>
            <Select value={orderId} onValueChange={(v)=>{ setOrderId(v); setConfirmQty(""); }}>
              <SelectTrigger data-testid="pack-order-select" className="rounded-sm mt-1 bg-white"><SelectValue placeholder="Select scanned order"/></SelectTrigger>
              <SelectContent>
                {pending.map(o=><SelectItem key={o.id} value={o.id}>{o.order_no} — {o.retailer_name}</SelectItem>)}
              </SelectContent>
            </Select>

            {selected && (
              <div className="mt-3 bg-white border border-violet-200 rounded-sm px-3 py-2 text-xs text-violet-900" data-testid="pack-scanned-info">
                Scanned quantity: <b className="font-mono text-sm">{scannedQty}</b>
              </div>
            )}

            <label className="label-cap block mt-4">Confirm Quantity *</label>
            <Input
              data-testid="pack-confirm-qty-input"
              type="number"
              value={confirmQty}
              onChange={(e)=>setConfirmQty(e.target.value)}
              placeholder={selected ? `Enter ${scannedQty} to confirm` : "Select an order first"}
              disabled={!selected}
              className={`mt-1 bg-white ${mismatch ? "border-rose-500 focus-visible:ring-rose-500" : ""}`}
            />
            {mismatch && (
              <div className="text-xs text-rose-600 mt-1" data-testid="pack-mismatch-hint">
                Doesn't match scanned quantity ({scannedQty})
              </div>
            )}

            <Button
              data-testid="pack-submit-btn"
              onClick={submit}
              disabled={!selected || mismatch || confirmQty === ""}
              className="w-full mt-4 bg-violet-600 hover:bg-violet-700 rounded-sm"
            >
              <ArchiveBox size={16} weight="bold" className="mr-1"/> Mark as Packed <ArrowRight size={16} className="ml-1"/>
            </Button>
          </div>

          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm">
            <div className="px-5 py-3 border-b border-slate-200"><div className="label-cap">Awaiting Packing · {pending.length}</div></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left label-cap border-b border-slate-200 bg-slate-50">
                  <th className="px-4 py-2">Order No</th><th className="px-4 py-2">Retailer</th>
                  <th className="px-4 py-2">Scanning Qty</th>
                  <th className="px-4 py-2">Boxes</th>
                  <th className="px-4 py-2">Scanned By</th><th className="px-4 py-2">Scanned At</th>
                </tr></thead>
                <tbody data-testid="pack-pending-tbody">
                  {pending.map(o=>(
                    <tr key={o.id} className={`border-b border-slate-100 hover:bg-violet-50 ${o.id === orderId ? "bg-violet-50" : ""}`}>
                      <td className="px-4 py-2 font-mono">{o.order_no}</td>
                      <td className="px-4 py-2">{o.retailer_name}</td>
                      <td className="px-4 py-2 font-mono font-semibold">{o.scanning?.total_pieces ?? "—"}</td>
                      <td className="px-4 py-2 font-mono">{o.scanning?.total_boxes ?? "—"}</td>
                      <td className="px-4 py-2 text-xs">{nameFor(o.scanning?.mobile)}</td>
                      <td className="px-4 py-2 text-xs text-slate-600">{o.scanning && new Date(o.scanning.at).toLocaleString()}</td>
                    </tr>
                  ))}
                  {pending.length===0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Nothing to pack</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
